import { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Modal } from '@/common';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { AssetItem } from '@/pages/Event/Asset/AssetItem';
import { AssetsTagFilter } from '@/pages/Event/Asset/AssetTagFilter';
import { AssetTagItem } from '@/pages/Event/Asset/AssetTagItem';
import { Asset, SelectOptionProps, ThemeProp } from '@/types';
import { TestIds } from '@/shared/test-ids';

type AssetsModalProps = {
  isOpen: boolean;
  usePublicAssets: boolean;
  isBackgroundAsset: boolean;
  assets: Asset[];
  selectedAsset: Asset | null;
  setSelectedAsset: (asset: Asset | null) => void;
  onClose: () => void;
  onSubmit: () => void;
  getAssetsForModal: (assets: Asset[], isBackgroundAsset: boolean) => Asset[];
  selectedTags: SelectOptionProps[] | undefined;
  setSelectedTags: Dispatch<SetStateAction<SelectOptionProps[] | undefined>>;
};

export const AssetsModal = ({
  isOpen,
  usePublicAssets,
  isBackgroundAsset,
  assets,
  selectedAsset,
  setSelectedAsset,
  onClose,
  onSubmit,
  getAssetsForModal,
  setSelectedTags,
  selectedTags
}: AssetsModalProps) => {
  const [t] = useTranslation('common');
  const { eventId } = useParams();

  const handleTagRemove = (tag: SelectOptionProps) => {
    if (selectedTags?.includes(tag)) {
      setSelectedTags(selectedTags.filter((t) => t?.value !== tag?.value));
    }
  };

  return (
    <Modal
      onClose={onClose}
      isOpen={isOpen}
      handleSubmit={onSubmit}
      hasButton
      modalClasses='md:w-[60vw] w-[90vw]'
      label={t(usePublicAssets ? 'publicAssets' : 'uploadedContent')}
      description={t(
        isBackgroundAsset
          ? 'pleaseChoosePhotoToUseAsBackground'
          : 'pleaseChoosePhotoToUseAsPortrait'
      )}
      height='h-full'
      buttonType={ButtonVariant.SECONDARY}
      buttonText={t('confirm')}
      buttonClasses='flex justify-end'
      isButtonDisabled={assets?.length === 0}
      isModalWithSearchAndFilters={true}>
      <div className='mb-4 flex flex-col'>
        {eventId && (
          <div className='ml-1'>
            <AssetsTagFilter
              setSelectedTags={setSelectedTags}
              selectedTags={selectedTags}
              eventId={eventId}
            />
          </div>
        )}
        {selectedTags && selectedTags.length > 0 && (
          <div className='mt-2 flex flex-row justify-start'>
            {selectedTags.map((tag) => (
              <AssetTagItem
                tag={tag}
                handleTagRemove={handleTagRemove}
                key={tag?.value as number}
              />
            ))}
          </div>
        )}
      </div>

      <div className='custom-scrollbar flex-1 overflow-y-auto'>
        <div data-testid={TestIds.coverPhotos.assetsModalGrid} className={'mr-3 mt-4 grid grid-cols-3 gap-3 pr-5 md:mr-0 md:grid-cols-5'}>
          {getAssetsForModal(assets, isBackgroundAsset).map((asset: Asset) => (
            <AssetItem
              asset={asset}
              key={asset.id}
              assetType={asset.assetType}
              canBeDeleted={false}
              canBeEdited={false}
              setSelectedAsset={
                setSelectedAsset as Dispatch<SetStateAction<Asset | ThemeProp | null>>
              }
              selectedAsset={selectedAsset as Asset}
              selectedVideoUrl={''}
            />
          ))}
        </div>
      </div>
    </Modal>
  );
};
